import * as React from 'react'
import { ReactElement, useContext, useEffect } from 'react'
import { Stack, StackItem } from '@fluentui/react'
import Loading from '../../components/Loading'
import { useRouteMatch } from 'react-router-dom'
import useFileData, { FileData } from '../../hooks/file/useFileData'
import useFilePages, { FilePageProps } from '../../hooks/file/useFilePages'
import ViewFileHeader from '../../components/File/ViewFileHeader'
import ViewFilePivotItems from '../../components/File/ViewFilePivotItems'
import { MessageBar } from '@fluentui/react-components'

// @ts-expect-error("dompurify exports differ from what ts-lint is expecting and cannot see the default export")
import DOMPurify from 'dompurify'
import { AppAction } from '../../config/appConfig'
import { MenuItemKey } from '../../components/MainMenu'
import { useDispatch } from 'react-redux'
import useEntity from '../../hooks/useEntity'
import { BaseOverviewCount } from '../BaseOverview/BaseOverview'
import FileContext from '../../context/FileContext'

export type ViewFileProps = {
    file: FileData
    fileId: string
    activePage: FilePageProps
}

export default function ViewFile(): ReactElement {
    const routeMatch = useRouteMatch<{ fileId: string }>()
    const fileId = routeMatch.params?.fileId

    const dispatch = useDispatch()
    const { activePage } = useFilePages(fileId)
    const { file, getActivePageAction } = useFileData(fileId, activePage)

    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Files] })
    }, [])

    // Publish the open dossier so dialogs hosted outside this screen (the task popup in PopupProvider) can resolve
    // it without fetching, and so its documents can be cached for as long as this screen is mounted (HAM-4295).
    const { setCurrentFile, setNoteCount } = useContext(FileContext)
    useEffect(() => {
        if (file.current.loaded) {
            setCurrentFile(file.current.data)
        }
    }, [file.current.loaded])

    // The note tab shows how many notes the dossier has without opening it, so the count is fetched here.
    // Once the overview is open it keeps the number up to date itself after every change (HAM-3476).
    const [noteCount] = useEntity<BaseOverviewCount>(() => ({ url: '/note/count', params: { files: fileId } }), fileId || false)
    useEffect(() => {
        setNoteCount(noteCount.data?.total)
    }, [noteCount.data?.total])

    if (!file.current.loaded || !activePage) {
        return <Loading />
    }

    return (
        <Stack tokens={{ childrenGap: 's1' }}>
            <MessageBar intent="warning" layout={'multiline'}>
                Deze versie is per 1 november 2026 niet meer beschikbaar
            </MessageBar>
            <ViewFileHeader file={file} fileId={fileId} activePage={activePage} />
            {file.current.data.notification && (
                <MessageBar intent="warning" layout={'multiline'}>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(file.current.data.notification)?.replaceAll('\n', '<br/>'),
                        }}
                    ></div>
                </MessageBar>
            )}
            <ViewFilePivotItems file={file} fileId={fileId} activePage={activePage} />
            <StackItem>{activePage?.content(file, getActivePageAction())}</StackItem>
        </Stack>
    )
}
